/**
 * Created by rathompson on 1/5/22
 * File: ActivationDetails
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

// import statement lib, local then scss or css
import { useCallback, useContext, useMemo, useState } from 'react'
import styled from 'styled-components'
import { Body, noop } from '@panwds/react-ui'
import AppContext from '../../../../../common/context/AppContext'
import StringTemplate from '../../../../../common/utils/StringTemplate'
import { indexArray } from '../../../../../common/utils/Utils'

import {
  SASE_SELECT_APP_REGION_SUBTEXT, SASE_SELECT_CDL_SUBTEXT, SASE_SELECT_REGION_SUBTEXT, SINGLE_TENANT_DROPDOWN_HEADERS,
} from '../../../../constants/LabelConstants'
import DividerLine from '../../../../../common/components/dividerLine/DividerLine'
import SubscriptionService from '../../../../services/SubscriptionService'
import { ADD_NEW } from '../../../constants/SubscriptionConstant'
import SingleTenantCSPDropDown from './SingleTenantCSPDropDown'
import SingleTenantRegionDropDown from './SingleTenantRegionDropDown'
import SingleTenantTsgDropDown from './SingleTenantTsgDropDown'
import SingleTenantCDLDropDown from './SingleTenantCDLDropDown'
import { FF_CDL_TSG_MIGRATION } from '../../../../../common/utils/FeatureFlags'

const Details = styled.div`
  button {
    justify-content: space-between;
    color: #333333;
  }

  .tw-font-lato.tw-text-xs.tw-space-y-1 {
    width: 100%;
  }
`

const ActivationDetails = ({
  license,
  loading,
  licenseContext,
  apps,
  setLoading,
  setSelectedTSGInstances,
  setError: setPageError,
  ...rest
}) => {
  const [appRegions, setAppRegions] = useState({})
  const { getAppName } = useContext(AppContext)

  const [instances, setInstance] = useState([])
  const emptyInstances = indexArray([], 'app_id')
  const migrateCDLFromCSP = useMemo(() => {
    return FF_CDL_TSG_MIGRATION && apps[licenseContext]?.setup?.extra?.migrate_cdl
  }, [apps, licenseContext])

  const changeTsgHandler = useCallback(async (tsg_id) => {
    setLoading(true)
    try {
      if (tsg_id !== ADD_NEW) {
        const { data } = await SubscriptionService.instances.list({ tsg_id })
        setInstance(indexArray(data, 'app_id'))
        setSelectedTSGInstances(indexArray(data, 'app_id'))
      }
      else {
        setInstance(emptyInstances)
        // when tsg selection is create new
        setSelectedTSGInstances(emptyInstances)
      }
    }
    catch (error) {
      setPageError(error.message)
    }
    setLoading(false)
  }, [emptyInstances, setLoading, setPageError, setSelectedTSGInstances])

  return (
    <Details className="tw-p-3">
      <div className="tw-flex">
        <div className="tw-flex-1">
          <div className="tw-flex tw-flex-col tw-gap-2 tw-w-max" style={{ minWidth: '50%' }}>
            <SingleTenantTsgDropDown
              {...{
                licenseContext,
                loading,
                apps,
                setPageError,
                changeTsgHandler,
                selectedTSGInstances: instances,
                ...rest
              }}
            />
            <SingleTenantCSPDropDown
              {...{
                licenseContext,
                loading,
                apps,
                setAppRegions,
                selectedTSGInstances: instances,
                setPageError,
                ...rest
              }}
            />
            {migrateCDLFromCSP &&
              <SingleTenantCDLDropDown
                {...{
                  licenseContext,
                  loading,
                  apps,
                  setPageError,
                  changeTsgHandler,
                  selectedTSGInstances: instances,
                  setLoading,
                  ...rest
                }}
              />
            }
            <SingleTenantRegionDropDown
              {...{
                licenseContext,
                loading,
                apps,
                setAppRegions,
                selectedTSGInstances: instances,
                appRegions,
                migrateCDLFromCSP,
                ...rest
              }}
            />
          </div>
        </div>
        <div className="tw-flex tw-flex-col tw-gap-2" style={{ flex: 2 }}>
          {SINGLE_TENANT_DROPDOWN_HEADERS.map((each, index) => {
            return <div className="tw-flex tw-items-end tw-flex-1" key={index}>
              <Body addClassName="tw-mb-2">{each}</Body>
            </div>
          })}
          {migrateCDLFromCSP && <div className="tw-flex tw-items-end tw-flex-1">
            <Body addClassName="tw-mb-2">{SASE_SELECT_CDL_SUBTEXT}</Body>
          </div>
          }
          <div className="tw-flex tw-items-end tw-flex-1">
            <Body addClassName="tw-mb-2"> {SASE_SELECT_REGION_SUBTEXT} </Body>
          </div>
          {(Object.keys(appRegions)?.length) ? Object.keys(appRegions).map((each, index) => {
            return <div className="tw-flex tw-items-end tw-flex-1" key={index}>
              <Body addClassName="tw-mb-2">{StringTemplate(SASE_SELECT_APP_REGION_SUBTEXT, [getAppName(each)])}</Body>
            </div>
          }) : <></>}
        </div>
      </div>
      <DividerLine />
    </Details>
  )
}

ActivationDetails.propTypes = {}

ActivationDetails.defaultProps = {
  license: {},
  licenses: [],
  cspDropDownData: [],
  tenants: [],
  loading: false,
  licenseContext: '',
  apps: {},
  onTSGChange: noop,
  supportedRegions: [],
}
export default ActivationDetails
